/**
 * Mutation hook for CSV出力 on 訪問予定表(施術師毎) — downloads CSV with service-type-aware file name.
 */

import { useMutation } from '@tanstack/react-query';
import useAppToast from '@/hooks/useAppToast';
import { useClinicContext } from '@/hooks/main/useClinicContext';
import { useTheme } from '@shared/providers/theme/ThemeContext';
import {
  SERVICE_TYPE_A2,
  SERVICE_TYPE_R2,
} from '@/constants/serviceType';
import { CSV_SUCCESS_MESSAGE, CSV_ERROR_MESSAGE } from '@/constants/messages';
import { exportHomeVisitMassagersCsv } from '@/services/main/homeVisitMassager';
import type { ExportHomeVisitMassagerCsvPayload } from '@/pages/main/homeVisitMassager/types';
import { downloadBlob } from '@/utils/blobDownload';

function resolveServiceType(theme: { serviceType?: string } | undefined): string {
  if (theme?.serviceType === SERVICE_TYPE_A2 || theme?.serviceType === SERVICE_TYPE_R2) {
    return theme.serviceType;
  }
  return typeof localStorage !== 'undefined'
    ? localStorage.getItem('service_type') || SERVICE_TYPE_A2
    : SERVICE_TYPE_A2;
}

function fallbackCsvFileName(serviceType: string): string {
  return serviceType === SERVICE_TYPE_R2
    ? '予定表（マッサージ師毎）.csv'
    : '予定表（はり・きゅう師毎）.csv';
}

export function useExportHomeVisitMassagerCsvMutation() {
  const { tenant, clinicNumber } = useClinicContext();
  const theme = useTheme();
  const toast = useAppToast();

  const { mutateAsync, isPending: isLoading } = useMutation<
    { blob: Blob; filename: string | null },
    Error,
    ExportHomeVisitMassagerCsvPayload
  >({
    mutationFn: payload =>
      exportHomeVisitMassagersCsv(tenant, clinicNumber, payload, resolveServiceType(theme)),
    onSuccess: () => {
      toast.success(CSV_SUCCESS_MESSAGE);
    },
    onError: () => {
      toast.error(CSV_ERROR_MESSAGE);
    },
  });

  const triggerExport = async (payload: ExportHomeVisitMassagerCsvPayload) => {
    const serviceType = resolveServiceType(theme);
    try {
      const { blob, filename } = await mutateAsync(payload);
      downloadBlob(blob, filename || fallbackCsvFileName(serviceType));
    } catch {
      // onError already handles toast
    }
  };

  return { triggerExport, isLoading };
}
